import { useEffect, useRef, useState } from "react";
import { LuImage } from "react-icons/lu";
import { FiEdit3, FiVolume2 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import ImagePreviewThumb from "./ImagePreviewThumb";
import {
    MEDIA_BTN_IMAGE_MD,
    MEDIA_BTN_IMAGE_SM,
    MEDIA_BTN_AUDIO_MD,
    MEDIA_BTN_AUDIO_SM,
    MEDIA_BTN_AUDIO_ACTIVE_MD,
    MEDIA_BTN_AUDIO_ACTIVE_SM,
} from "./mediaIconButtonClasses";
import { isMp3File } from "./mediaValidation";

type MediaEditGroupProps = {
    imageInputId: string;
    imageFile: File | null;
    imageUrl: string | null;
    imageAlt: string;
    onImageChange: (file: File) => void;
    /** Omit to hide audio entirely (e.g. flyingFruits choices) */
    audioInputId?: string;
    audioFile?: File | null;
    audioUrl?: string | null;
    onAudioChange?: (file: File) => void;
    onImageDelete?: () => void;
    onAudioDelete?: () => void;
    disableDelete?: boolean;
    size?: "md" | "sm";
};

type ModalType = "image" | "audio";

/**
 * Renders image + audio upload buttons, or previews when media is loaded.
 * - No media: shows upload icon buttons.
 * - Has image: thumbnail with in-place hover overlay (no floating popover gap).
 * - Has audio: play button + always-visible "แก้ไข" link (works after reload from server URL).
 */
export default function MediaEditGroup({
    imageInputId,
    imageFile,
    imageUrl,
    imageAlt,
    onImageChange,
    audioInputId,
    audioFile = null,
    audioUrl = null,
    onAudioChange,
    onImageDelete,
    onAudioDelete,
    disableDelete = false,
    size = "md",
}: MediaEditGroupProps) {
    const hasImage = !!(imageFile || imageUrl);
    const hasAudio = !!(audioFile || audioUrl);
    const withAudio = !!audioInputId;
    const thumbSize = size === "md" ? "h-7 w-7" : "h-6 w-6";
    const iconSize = size === "md" ? 18 : 16;
    const imgBtnClass = size === "md" ? MEDIA_BTN_IMAGE_MD : MEDIA_BTN_IMAGE_SM;
    const audioBtnClass = size === "md" ? MEDIA_BTN_AUDIO_MD : MEDIA_BTN_AUDIO_SM;
    const audioBtnActiveClass = size === "md" ? MEDIA_BTN_AUDIO_ACTIVE_MD : MEDIA_BTN_AUDIO_ACTIVE_SM;
    const [activeModal, setActiveModal] = useState<ModalType | null>(null);
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const audioInputRef = useRef<HTMLInputElement | null>(null);
    const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
    const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
    const imagePreviewSrc = imageObjectUrl ?? imageUrl;
    const audioPreviewSrc = audioObjectUrl ?? audioUrl;

    useEffect(() => {
        if (!imageFile) {
            setImageObjectUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(imageFile);
        setImageObjectUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    useEffect(() => {
        if (!audioFile) {
            setAudioObjectUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(audioFile);
        setAudioObjectUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [audioFile]);

    useEffect(() => {
        if (!activeModal) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setActiveModal(null);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [activeModal]);

    const handleSelectImageFile = () => {
        imageInputRef.current?.click();
    };

    const handleSelectAudioFile = () => {
        audioInputRef.current?.click();
    };

    return (
        <>
            <input
                ref={imageInputRef}
                id={imageInputId}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onImageChange(f);
                    e.currentTarget.value = "";
                }}
            />
            {withAudio && (
                <input
                    ref={audioInputRef}
                    id={audioInputId}
                    type="file"
                    accept=".mp3,audio/mpeg"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && isMp3File(f)) onAudioChange?.(f);
                        else if (f) window.alert("อัปโหลดได้เฉพาะไฟล์เสียง .mp3");
                        e.currentTarget.value = "";
                    }}
                />
            )}

            <div className="flex shrink-0 items-center gap-2">
                {!hasImage ? (
                    <label htmlFor={imageInputId} className={imgBtnClass}>
                        <LuImage size={iconSize} />
                    </label>
                ) : (
                    <button
                        type="button"
                        title="ดูรูปภาพ"
                        onClick={() => setActiveModal("image")}
                        className="group relative shrink-0 cursor-pointer overflow-visible rounded"
                    >
                        <ImagePreviewThumb file={imageFile} url={imageUrl} alt={imageAlt} className={thumbSize} />
                        <span className="pointer-events-none absolute inset-0 rounded bg-black/0 transition-colors group-hover:bg-black/10" />
                    </button>
                )}

                {withAudio &&
                    (!hasAudio ? (
                        <label htmlFor={audioInputId} className={audioBtnClass} title="เพิ่มเสียง">
                            <FiVolume2 size={iconSize} />
                        </label>
                    ) : (
                        <button
                            type="button"
                            title="ดูเสียง"
                            onClick={() => setActiveModal("audio")}
                            className={audioBtnActiveClass}
                        >
                            <FiVolume2 size={iconSize} />
                        </button>
                    ))}
            </div>

            {activeModal ? (
                <div
                    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setActiveModal(null)}
                >
                    <div
                        className="relative w-full max-w-[560px] rounded-3xl border border-[#d9d9d9] bg-white p-6 shadow-[0_24px_60px_rgba(0,0,0,0.3)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex min-h-[240px] items-center justify-center rounded-2xl bg-[#f7f8fa] p-4">
                            {activeModal === "image" ? (
                                imagePreviewSrc ? (
                                    <img
                                        src={imagePreviewSrc}
                                        alt={imageAlt}
                                        className="max-h-[380px] w-auto max-w-full rounded-xl border border-[#dedede] object-contain bg-white shadow-sm"
                                    />
                                ) : (
                                    <div className="text-sm text-[#8b8b8b]">ไม่พบรูปภาพ</div>
                                )
                            ) : audioPreviewSrc ? (
                                <audio
                                    src={audioPreviewSrc}
                                    controls
                                    autoPlay
                                    className="w-full max-w-[420px]"
                                />
                            ) : (
                                <div className="text-sm text-[#8b8b8b]">ไม่พบไฟล์เสียง</div>
                            )}
                        </div>

                        <div className="mt-6 flex items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeModal === "image") {
                                        onImageDelete?.();
                                    } else {
                                        onAudioDelete?.();
                                    }
                                    setActiveModal(null);
                                }}
                                disabled={
                                    disableDelete ||
                                    (activeModal === "image" ? !onImageDelete : !onAudioDelete)
                                }
                                className="inline-flex min-w-[90px] items-center justify-center gap-1 rounded-lg border border-[#ffcaca] bg-[#fff2f2] px-4 py-2 text-sm font-medium text-[#d64b4b] transition-colors hover:bg-[#ffe7e7] disabled:cursor-not-allowed disabled:opacity-45"
                            >
                                <RiDeleteBinLine size={16} />
                                ลบ
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeModal === "image") {
                                        handleSelectImageFile();
                                    } else {
                                        handleSelectAudioFile();
                                    }
                                }}
                                disabled={activeModal === "audio" && !withAudio}
                                className="inline-flex min-w-[90px] items-center justify-center gap-1 rounded-lg border border-[#cde6f8] bg-[#eef7ff] px-4 py-2 text-sm font-medium text-[#2c83be] transition-colors hover:bg-[#e1f1ff] disabled:cursor-not-allowed disabled:opacity-45"
                            >
                                <FiEdit3 size={16} />
                                แก้ไข
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveModal(null)}
                                className="inline-flex min-w-[90px] items-center justify-center rounded-lg border border-[#d8d8d8] bg-white px-4 py-2 text-sm font-medium text-[#4f4f4f] transition-colors hover:bg-[#f5f5f5]"
                            >
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
